import { FooterCell, Typography } from '@ui-components/neo.tax';
import { CellProps, Column } from 'react-table';

import { type AscProject, type AscProjectTeamMember } from '@neo.tax/asc/hooks';
import { formatContributorName } from '@neo.tax/shared';

import { AscTeamMemberAllocation } from '../AscTeamMemberAllocation';

type AscTeamMembersTableColumns = {
  columns: Column<AscProjectTeamMember>[] | null;
};

export const useAscTeamMembersTableColumns = ({
  ascProject,
  teamMembers,
}: {
  teamMembers: AscProjectTeamMember[] | null;
  ascProject: AscProject;
}): AscTeamMembersTableColumns => {
  const numberOfTeamMembers = teamMembers?.length ?? 0;
  const sumOfAllocations = teamMembers?.reduce((a, b) => a + b.allocationPercent, 0) ?? 0;
  const averageProjectAllocationPercent = numberOfTeamMembers === 0 ? 0 : sumOfAllocations / numberOfTeamMembers;

  return {
    columns: [
      {
        id: 'team-member',
        width: 'minmax(150px, 1fr)',
        Header: 'Name',
        accessor: (teamMember: AscProjectTeamMember): AscProjectTeamMember['contributorName'] =>
          teamMember.contributorName,
        Cell: ({ value }: CellProps<AscProjectTeamMember, string>): JSX.Element => (
          <Typography
            title={formatContributorName(value)}
            fontWeight="medium"
            fontSize="sm"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
          >
            {formatContributorName(value)}
          </Typography>
        ),
        Footer: (): JSX.Element => <FooterCell preText="COUNT" text={numberOfTeamMembers} />,
      },
      {
        id: ascProject.id,
        width: 110,
        Header: '% of Time',
        accessor: (teamMember: AscProjectTeamMember): AscProjectTeamMember => teamMember,
        Cell: ({ value }: CellProps<AscProjectTeamMember, AscProjectTeamMember>): JSX.Element => (
          <AscTeamMemberAllocation ascProject={ascProject} ascProjectTeamMember={value} />
        ),
        Footer: (): JSX.Element => (
          <FooterCell preText="AVG" text={`${Math.round(averageProjectAllocationPercent)}%`} alignText="end" />
        ),
      },
    ],
  };
};
